import { JSX, useEffect, useState } from "react";
import { Action, Func, VoidAction } from "./func";
import StyledButton, { ButtonStyle } from "../components/StyledButton";
import { SxProps, Theme, Typography } from "@mui/material";

type errorDict<T> = {
  [key in keyof T]?: boolean;
};

export type useInputStateType<T, K extends keyof T> = {
  value: T[K];
  setValue: (value: T[K]) => void;
  setError: (error: boolean) => void;
};

export type useInputType<T> = {
  value: T;
  getState: <K extends keyof T>(key: K) => useInputStateType<T, K>;
  submitButton: Func<{ label?: string; sx?: SxProps<Theme>; buttonStyle?: ButtonStyle }, JSX.Element>;
  submit: VoidAction;
  forceSubmit: (submitValue: T) => void;
};

export type RequestFile = {
  file: File;
  uuid: string;
};

export default function useInput<T>(data: T, submit: Action<T>, deps?: React.DependencyList): useInputType<T> {
  const [value, setValue] = useState(() => data);
  const [errors, setErrors] = useState<errorDict<T>>({});
  useEffect(() => setValue(data), deps ?? []);

  const getState = <K extends keyof T>(key: K) => {
    return {
      value: value[key],
      setValue: (value: T[K]) => setValue((prev) => ({ ...prev, [key]: value })),
      setError: (error: boolean) => setErrors((prev) => ({ ...prev, [key]: error })),
    };
  };

  return {
    value,
    getState,
    submit: () => submit(value), 
    forceSubmit: (submitValue: T) => submit(submitValue),
    submitButton: ({ label = "保存", sx, buttonStyle = "primary" }: { label?: string; sx?: SxProps<Theme>; buttonStyle?: ButtonStyle }) => (
      <StyledButton
        onClick={() => {
          submit(value);
        }}
        buttonStyle={buttonStyle}
        sx={sx}
        disabled={Object.values(errors).some((e) => e)}
      >
        <Typography>{label}</Typography>
      </StyledButton>
    ),
  };
}
